/**
 * Technology Capability Detail Route
 *
 * Displays comprehensive technology capability metadata
 */

import { useState } from 'react';
import { createFileRoute, useNavigate, Outlet, useLocation } from '@tanstack/react-router';
import { useAllTags, useTechnologyCapability } from '../../graphql/queries';
import { useApolloClient } from '@apollo/client/react';
import {
  DetailSkeleton,
  EmptyState,
  DeleteConfirmationDialog,
  PermissionGate,
} from '../../components/shared';
import { TagBadge } from '../../components/tags';
import { deleteTechnologyCapability, ApiError } from '../../lib/apiClient';
import '@tylertech/forge/card';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import '@tylertech/forge/divider';
import './technologyCapabilityId.css';

export const Route = createFileRoute('/technology-capabilities/$technologyCapabilityId')({
  component: TechnologyCapabilityDetailPage,
});

export { TechnologyCapabilityDetailPage };

function TechnologyCapabilityDetailPage(): JSX.Element {
  const { technologyCapabilityId } = Route.useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const apolloClient = useApolloClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If on a child route (edit), render only the outlet
  const isChildRoute = location.pathname.endsWith('/edit');

  // Fetch technology capability data
  const { data, loading, error: fetchError } = useTechnologyCapability(technologyCapabilityId);
  const technologyCapability = data?.technologyCapability;

  // Tags are now catalog tag ids selected via TagPicker (issue #764), not
  // free-typed strings — resolve to the full Tag (name/color) for chip
  // display instead of showing the raw id.
  const { tags: allTags } = useAllTags();
  const assignedTags = (technologyCapability?.tags ?? [])
    .map((tagId) => allTags.find((tag) => tag.entityId === tagId))
    .filter((tag): tag is NonNullable<typeof tag> => tag !== undefined);

  const CardElement = 'forge-card' as React.ElementType;
  const IconElement = 'forge-icon' as React.ElementType;
  const DividerElement = 'forge-divider' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;

  if (isChildRoute) {
    return <Outlet />;
  }

  // Loading state
  if (loading) {
    return (
      <div className="technology-capability-detail-page">
        <DetailSkeleton cards={3} />
      </div>
    );
  }

  // Error state
  if (fetchError) {
    return (
      <div className="technology-capability-detail-page">
        <EmptyState
          icon="error"
          title="Failed to load technology capability"
          description={fetchError.message}
          actionLabel="Back to Technology Capabilities"
          onAction={() =>
            navigate({ to: '/technology-capabilities', search: { page: 1, pageSize: 25 } })
          }
        />
      </div>
    );
  }

  // Not found state
  if (!technologyCapability) {
    return (
      <div className="technology-capability-detail-page">
        <EmptyState
          icon="search_off"
          title="Technology capability not found"
          description={`No technology capability found with ID: ${technologyCapabilityId}`}
          actionLabel="Back to Technology Capabilities"
          onAction={() =>
            navigate({ to: '/technology-capabilities', search: { page: 1, pageSize: 25 } })
          }
        />
      </div>
    );
  }

  // Format dates
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Handle edit action
  const handleEdit = (): void => {
    navigate({
      to: `/technology-capabilities/${technologyCapability.entityId}/edit` as string,
    });
  };

  // Handle delete action
  const handleDelete = (): void => {
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const handleConfirmDelete = async (): Promise<void> => {
    setIsDeleting(true);
    setError(null);

    try {
      // Soft delete by default
      await deleteTechnologyCapability(technologyCapabilityId, false);

      // Refetch GraphQL data to update cache
      await apolloClient.refetchQueries({
        include: ['ListTechnologyCapabilities'],
      });

      // Navigate back to technology capabilities list
      navigate({ to: '/technology-capabilities', search: { page: 1, pageSize: 25 } });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsDeleting(false);
    }
  };

  return (
    <div className="technology-capability-detail-page">
      {/* Header */}
      <div className="technology-capability-detail-header">
        <div>
          <ForgeButton
            type="button"
            variant="text"
            onClick={() =>
              navigate({ to: '/technology-capabilities', search: { page: 1, pageSize: 25 } })
            }
          >
            <IconElement name="arrow_back" aria-hidden="true" />
            Back to Technology Capabilities
          </ForgeButton>
          <h1 className="forge-typography--headline4">{technologyCapability.name}</h1>
          <p className="forge-typography--body2 page-header-subtitle">
            {technologyCapability.description || 'No description provided'}
          </p>
        </div>
        <div className="technology-capability-detail-actions">
          <PermissionGate permission="technology-capability:update">
            <ForgeButton type="button" variant="raised" onClick={handleEdit}>
              <IconElement name="edit" aria-hidden="true" />
              Edit
            </ForgeButton>
          </PermissionGate>
          <PermissionGate permission="technology-capability:delete">
            <ForgeButton type="button" variant="outlined" onClick={handleDelete}>
              <IconElement name="delete" aria-hidden="true" />
              Delete
            </ForgeButton>
          </PermissionGate>
        </div>
      </div>

      <div className="technology-capability-detail-content">
        {/* Overview Card */}
        <CardElement className="detail-card">
          <div className="card-header">
            <h2 className="forge-typography--headline6">Overview</h2>
          </div>
          <DividerElement />
          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Technology Capability ID</span>
              <span className="detail-value">{technologyCapability.entityId}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Domain ID</span>
              <span className="detail-value">{technologyCapability.domainId || '—'}</span>
            </div>
          </div>
        </CardElement>

        {/* Additional Information Card */}
        <CardElement className="detail-card">
          <div className="card-header">
            <h2 className="forge-typography--headline6">Additional Information</h2>
          </div>
          <DividerElement />
          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Tags</span>
              <span className="detail-value">
                {assignedTags.length > 0 ? (
                  <span className="technology-capability-tags">
                    {assignedTags.map((tag) => (
                      <TagBadge key={tag.entityId} tag={tag} />
                    ))}
                  </span>
                ) : (
                  '—'
                )}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Notes</span>
              <span className="detail-value">{technologyCapability.notes || '—'}</span>
            </div>
          </div>
        </CardElement>

        {/* Metadata Card */}
        <CardElement className="detail-card">
          <div className="card-header">
            <h2 className="forge-typography--headline6">Metadata</h2>
          </div>
          <DividerElement />
          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Created</span>
              <span className="detail-value">
                {formatDate(technologyCapability._createdAt)} by {technologyCapability._createdBy}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Last Updated</span>
              <span className="detail-value">
                {formatDate(technologyCapability._updatedAt)} by {technologyCapability._updatedBy}
              </span>
            </div>
          </div>
        </CardElement>
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          onConfirm={handleConfirmDelete}
          onCancel={() => setDeleteDialogOpen(false)}
          title="Delete Technology Capability"
          message="Are you sure you want to delete this technology capability?"
          warning="This action will soft delete the technology capability. It can be recovered by an administrator."
          itemName={technologyCapability.name}
          isDeleting={isDeleting}
        />
      )}

      {/* Error Banner */}
      {error && <div className="detail-error-toast">{error}</div>}
    </div>
  );
}
